import type { TranslationKey } from '../../core/i18n/translation';
import type { PortalNotification } from '../../core/models/portal-notification';

/**
 * Turns a stored notification — a type and a parameter bag — into a sentence in the language it is
 * being read in.
 *
 * **This is the half the server deliberately does not do.** RAICRM's notification table keeps a
 * `Message` rendered when the row was written, which on a bilingual product means the sentence is
 * in whatever language the actor happened to have. Rendering here means a customer who switches
 * language re-reads their whole history in the new one.
 *
 * An unknown type renders the generic line rather than nothing: a notification the client does not
 * recognise is still one the customer was meant to see, and a blank row is worse than a vague one.
 */
export function describeNotification(
    notification: PortalNotification,
    language: string,
    t: (key: TranslationKey) => string
): string {
    const parameters = notification.parameters;

    switch (notification.type) {
        case 'CaseMilestoneReached':
            return format(t('portal.notification.case_milestone'), {
                caseNumber: parameters.caseNumber ?? '',

                // The milestone was snapshotted in both languages when the row was written, so the
                // reader's language picks between two texts rather than translating one.
                milestone: (language === 'kk' ? parameters.milestoneKk : parameters.milestoneRu) ?? ''
            });

        case 'AppointmentConfirmed':
            // The date and time arrive already converted to the appointment's zone. A notification
            // written before a zone was configured carries neither, and says so without inventing
            // one.
            return parameters.scheduledDate === undefined || parameters.scheduledTime === undefined
                ? t('portal.notification.appointment_confirmed_no_time')
                : format(t('portal.notification.appointment_confirmed'), {
                      date: parameters.scheduledDate,
                      time: parameters.scheduledTime
                  });

        case 'AppointmentCancelled':
            return t('portal.notification.appointment_cancelled');

        case 'MessageReceived':
            return format(t('portal.notification.message_received'), {
                preview: parameters.preview ?? ''
            });

        case 'DocumentShared':
            // The title is the one parameter in this whole file that is free text rather than a
            // snapshotted translation: staff typed it, so neither language has a version of it.
            return format(t('portal.notification.document_shared'), {
                title: parameters.documentTitle ?? ''
            });

        default:
            return t('portal.notification.unknown');
    }
}

/** Replaces `{name}` placeholders. Anything unmatched is left as it stands rather than blanked. */
function format(template: string, values: Record<string, string>): string {
    return template.replace(/\{(\w+)\}/g, (match, name: string) => values[name] ?? match);
}
